import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Building2, ArrowRight, CheckCircle2, ShieldCheck, Globe2, Users, Sparkles, Palette, Calendar, Tag, Zap } from 'lucide-react';
import { BrandName } from '../components/BrandName';
import { RESIDENCY_REGIONS, getResidency } from '../lib/residency';
import { COUNTRIES_BY_REGION, REGION_ORDER, POPULAR_COUNTRIES } from '../lib/countries';
import { DISCIPLINES } from '../lib/disciplines';

/**
 * Public self-serve clinic onboarding with pricing tiers.
 *
 * Solo clinic — one practitioner, one location.
 * Multi clinic — multiple practitioners and/or multiple locations.
 * Both get 30-day free trial. Yearly billing saves ~20%.
 */

const PRICING = {
  solo: {
    label: 'Solo Practitioner',
    blurb: 'Entry-level — for the independent clinician working alone.',
    monthly: 20,
    yearly: 192,                       // 20 * 12 = 240 → save 48 = 20%
    savings: 'Save 48 €',
    period_label: '/user/month',
    currency: 'EUR',
    features: [
      'One practitioner, one location',
      'Up to 200 active patients',
      'Online booking + automated reminders',
      'Digital intake — Subjective + Objective + SOAP',
      'Treatment plans + 200-exercise library',
      'Home Exercise Programs (HEP) + video assignments',
      'Basic invoicing — GDPR & HIPAA-ready',
      'EN / PL / ES patient portal',
      'Custom logo on portal & invoices',
      'PROMs (outcome measures) + body chart',
      'AI clinical summaries (Claude Sonnet 4.5)',
      'Email support',
    ],
  },
  medium: {
    label: 'Medium Clinic',
    blurb: 'For growing clinics with a small team and shared schedule.',
    monthly: 39,
    yearly: 374,                       // 39 * 12 = 468 → save 94 = 20%
    savings: 'Save 94 €',
    period_label: '/user/month',
    currency: 'EUR',
    popular: true,
    features: [
      'Everything in Solo, plus:',
      'Up to 10 practitioners, 1-2 locations',
      'Unlimited active patients',
      'Receptionist + clinician roles + 2FA',
      'AI ICD-10 suggester (1,280+ codes)',
      'AI Scribe — ambient voice-to-chart',
      'Treatment Packages with auto-decrement',
      'Stripe Connect — direct deposits to your bank',
      'Promotions & marketing email blasts',
      'Per-location reporting (P&L, KPIs)',
      'Product catalogue / clinic storefront',
      'Membership & visit-pack billing',
      'Priority email + chat support',
    ],
  },
  multi: {
    label: 'Multi-Clinic Practice',
    blurb: 'For multi-location groups, franchises and physio networks.',
    monthly: 45,
    yearly: 432,                       // 45 * 12 = 540 → save 108 = 20%
    savings: 'Save 108 €',
    period_label_prefix: 'from ',
    period_label: '/user/month',
    currency: 'EUR',
    features: [
      'Everything in Medium, plus:',
      'Unlimited practitioners + locations',
      'Network-wide patient records & transfer',
      'Multi-disciplinary care timeline',
      'Layered body-chart progression view',
      'Group-level financial reporting & VAT exports',
      'Custom HEP video uploads (mobile camera)',
      'White-label patient portal (your domain)',
      'API + webhook access',
      'SSO / SAML for staff (on request)',
      'Dedicated onboarding manager',
      'SLA + named priority support',
    ],
  },
};

export default function ClinicOnboard() {
  const nav = useNavigate();
  const [plan, setPlan] = useState('solo');
  const [billing, setBilling] = useState('monthly');
  const [mode, setMode] = useState('magic');     // 'magic' (email link) | 'password' (instant)
  const [form, setForm] = useState({
    clinic_name: '', owner_name: '', owner_email: '',
    owner_password: '', country_code: 'PL',
    data_residency: 'eu',
    disciplines: ['physiotherapy'],
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);   // {email, magic_url_preview}
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      if (mode === 'magic') {
        const { data } = await api.post('/onboard/clinic/magic', {
          clinic_name: form.clinic_name,
          owner_name: form.owner_name,
          owner_email: form.owner_email,
          country_code: form.country_code,
          data_residency: form.data_residency,
          disciplines: form.disciplines,
          plan, billing,
        });
        setSent({ email: data.email, magic_url_preview: data.magic_url_preview });
      } else {
        const { data } = await api.post('/onboard/clinic', { ...form, plan, billing });
        await api.post('/auth/login', { email: data.email, password: form.owner_password });
        // Iter 115 — new admins land on the required branding wizard first.
        nav('/onboard/branding');
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not create clinic.');
    } finally { setBusy(false); }
  };

  // Compute these inline below to dodge a build-time decorator that can
  // cache the initial value on the JSX node. Use `key={plan}` on the
  // dependent block to force a fresh subtree on plan change.

  return (
    <div className="min-h-screen bg-[#FAF6EE]">
      {/* HERO */}
      <header className="bg-[#003D80] text-white pt-12 pb-16 px-6 md:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#FFB000]">PHYSIOGENIC NETWORK</div>
          <h1 className="text-4xl lg:text-6xl font-heading font-bold mt-4 leading-tight max-w-3xl text-[#FFB000]" data-testid="onboard-hero-title">
            Launch your clinic on <BrandName/> in 60 seconds.
          </h1>
          <p className="text-white/80 mt-4 max-w-2xl text-base">
            Everything you need to run a modern physiotherapy practice — yours, branded, ready in minutes.
            <strong className="text-white"> 30-day free trial. Cancel anytime.</strong>
          </p>
          <div className="mt-6 flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-white">
              <Calendar className="w-3.5 h-3.5 text-[#FFB000]"/> 30-day free trial
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-white">
              <Palette className="w-3.5 h-3.5 text-[#FFB000]"/> Your logo, your colors
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-white">
              <Globe2 className="w-3.5 h-3.5 text-[#FFB000]"/> EN / PL / ES via AI translation
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-white">
              <ShieldCheck className="w-3.5 h-3.5 text-[#FFB000]"/> GDPR + HIPAA-ready
            </span>
          </div>
        </div>
      </header>

      {/* PRICING + FORM */}
      <main className="max-w-6xl mx-auto -mt-8 px-6 md:px-10 pb-12">
        {/* Billing toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-1 bg-white border border-soft rounded-full p-1 shadow-sm" data-testid="billing-toggle">
            <button onClick={() => setBilling('monthly')}
                    className={`text-xs px-4 py-2 rounded-full ${billing === 'monthly' ? 'bg-brand text-white' : 'text-fg-muted'}`}
                    data-testid="billing-monthly">
              Billed monthly
            </button>
            <button onClick={() => setBilling('yearly')}
                    className={`text-xs px-4 py-2 rounded-full inline-flex items-center gap-1 ${billing === 'yearly' ? 'bg-brand text-white' : 'text-fg-muted'}`}
                    data-testid="billing-yearly">
              Billed yearly <span className="text-[10px] uppercase tracking-wider bg-[#FFB000] text-[#003D80] px-1.5 py-0.5 rounded">-20%</span>
            </button>
          </div>
        </div>

        {/* Plan cards — Iter 73: 3 tiers (Solo / Medium / Multi) at per-user pricing. */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {Object.entries(PRICING).map(([k, t]) => {
            const selected = plan === k;
            const showPrice = billing === 'yearly' ? t.yearly : t.monthly;
            const periodLabel = billing === 'yearly' ? '/year' : t.period_label || '/month';
            const prefix = t.period_label_prefix || '';
            return (
              <button
                key={k}
                onClick={() => setPlan(k)}
                className={`text-left surface border-2 rounded-2xl p-6 transition relative ${
                  selected ? 'border-brand shadow-lg ring-4 ring-brand/10' : 'border-soft hover:border-brand/40'
                }`}
                data-testid={`plan-${k}`}
              >
                {t.popular && (
                  <div className="absolute -top-3 left-6 text-[10px] uppercase tracking-wider bg-[#FFB000] text-[#003D80] px-2 py-1 rounded font-bold">
                    Most popular
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {k === 'solo' ? <Users className="w-5 h-5 text-brand"/> :
                     k === 'medium' ? <Building2 className="w-5 h-5 text-brand"/> :
                     <Sparkles className="w-5 h-5 text-brand"/>}
                    <h3 className="font-heading font-bold text-xl">{t.label}</h3>
                  </div>
                  {selected && <CheckCircle2 className="w-5 h-5 text-brand"/>}
                </div>
                <p className="text-sm text-fg-muted mt-1">{t.blurb}</p>
                <div className="mt-4 flex items-baseline gap-1.5" key={`p-${k}-${billing}`}>
                  {prefix && <span className="text-xs text-fg-muted self-center">{prefix}</span>}
                  <span className="text-4xl font-heading font-bold text-fg" data-testid={`price-${k}`}>{showPrice}€</span>
                  <span className="text-sm text-fg-muted">{periodLabel}</span>
                </div>
                {billing === 'yearly' && (
                  <div className="text-[11px] text-[#1B5E20] font-semibold mt-0.5">{t.savings}</div>
                )}
                <ul className="mt-5 space-y-2 text-sm">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-brand shrink-0 mt-0.5"/>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {/* Form */}
        <div className="surface border border-soft rounded-2xl p-6 md:p-8 grid md:grid-cols-2 gap-8">
          <div key={`launch-${plan}-${billing}`}>
            <h2 className="text-2xl font-heading font-bold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-brand"/> Ready to launch?
            </h2>
            <p className="text-sm text-fg-muted mt-2">
              You picked <strong data-testid="ob-picked-plan">{PRICING[plan].label}</strong>
              {' · '}
              <strong data-testid="ob-picked-price">{PRICING[plan].period_label_prefix || ''}{billing === 'yearly' ? PRICING[plan].yearly : PRICING[plan].monthly}€</strong>
              {billing === 'yearly' ? '/year' : (PRICING[plan].period_label || '/month')}.
              Start your 30-day free trial right now — no credit card required to begin, add a card when your trial ends.
            </p>

            {/* Plan-specific highlights — swap on toggle so users see real-time
                confirmation of their choice. Iter 73 adds the Medium tier. */}
            <div className="mt-6 surface border border-soft rounded-lg p-4 text-xs space-y-2 bg-[#FFF8E7]" data-testid={`ob-plan-perks-${plan}`}>
              <div className="text-[10px] uppercase tracking-wider text-[#003D80] font-semibold mb-1">
                Why pick {PRICING[plan].label}?
              </div>
              {plan === 'solo' ? (
                <>
                  <PerkRow icon={Tag}      title="Lean, fast setup"
                                          body="One practitioner, one location — your whole clinic in 60 seconds. Upgrade to Medium or Multi any time as you grow."/>
                  <PerkRow icon={Zap}      title="AI clinical summaries included"
                                          body="Claude Sonnet 4.5 writes your visit notes, suggests ICD-10 codes, and turns voice into chart."/>
                  <PerkRow icon={Calendar} title="Cancel anytime"
                                          body="Export your data, drop the plan, no questions. Your patient records stay yours, always."/>
                </>
              ) : plan === 'medium' ? (
                <>
                  <PerkRow icon={Users}     title="Team-ready"
                                            body="Receptionist + clinician roles, 2FA, treatment packages and Stripe Connect payouts to your bank."/>
                  <PerkRow icon={Sparkles}  title="AI Scribe + ICD suggester"
                                            body="Ambient voice-to-chart and an AI ICD-10 suggester trained on 1,280+ physio/osteo/chiro codes."/>
                  <PerkRow icon={Building2} title="Per-location reporting"
                                            body="Run 1-2 locations under one login with separate P&L, KPIs, and staff scheduling."/>
                  <PerkRow icon={ShieldCheck} title="Priority support"
                                              body="Priority email + chat. A real human migrates your data from your old system on day one."/>
                </>
              ) : (
                <>
                  <PerkRow icon={Building2} title="Network-grade"
                                            body="Unlimited locations, network-wide patient records & transfers, group-level financial reporting and VAT exports."/>
                  <PerkRow icon={Sparkles}  title="Advanced clinical views"
                                            body="Multi-disciplinary care timeline + layered body-chart progression — only available on Multi."/>
                  <PerkRow icon={Palette}   title="White-label + API"
                                            body="Your own domain on the patient portal, custom branding everywhere, API + webhook access on request."/>
                  <PerkRow icon={ShieldCheck} title="SLA + named support"
                                              body="Dedicated onboarding manager, SSO / SAML on request, contracted SLA."/>
                </>
              )}
            </div>
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="onboard-form">
            {sent ? (
              <div className="space-y-4">
                <div className="surface border border-soft rounded-lg p-5 bg-[#E8F5E9]/40">
                  <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#1B5E20]"/> Check your inbox.
                  </h3>
                  <p className="text-sm mt-2">
                    We sent a magic sign-in link to <strong data-testid="ob-sent-email">{sent.email}</strong>.
                    Click it on this device to finish setting up <strong>{form.clinic_name}</strong>.
                  </p>
                  <p className="text-xs text-fg-muted mt-2">The link expires in 60 minutes and can only be used once.</p>
                  {sent.magic_url_preview && (
                    <details className="mt-3 text-xs">
                      <summary className="cursor-pointer text-fg-muted hover:text-brand">Didn't get the email? Use the direct link</summary>
                      <a href={sent.magic_url_preview} className="text-brand underline break-all block mt-1.5" data-testid="ob-magic-direct">{sent.magic_url_preview}</a>
                    </details>
                  )}
                </div>
                <button type="button" onClick={() => { setSent(null); setForm({...form, owner_email: ''}); }}
                        className="btn-secondary w-full text-sm" data-testid="ob-back">
                  Use a different email
                </button>
              </div>
            ) : (
              <>
                <Field label="Clinic name *"><input className="input" required minLength={2} value={form.clinic_name} onChange={e => set('clinic_name', e.target.value)} placeholder="Sunshine Physio Clinic" data-testid="ob-clinic-name" /></Field>
                <Field label="Your name *"><input className="input" required value={form.owner_name} onChange={e => set('owner_name', e.target.value)} data-testid="ob-owner-name" /></Field>
                <Field label="Email *"><input className="input" type="email" required value={form.owner_email} onChange={e => set('owner_email', e.target.value)} data-testid="ob-email" /></Field>
                {mode === 'password' && (
                  <Field label="Password *"><input className="input" type="password" required minLength={8} value={form.owner_password} onChange={e => set('owner_password', e.target.value)} placeholder="At least 8 characters" data-testid="ob-password" /></Field>
                )}
                <Field label="Country"><select className="input" value={form.country_code} onChange={e => set('country_code', e.target.value)} data-testid="ob-country">
                  <optgroup label="Popular">
                    {POPULAR_COUNTRIES.map(c => (
                      <option key={'pop-' + c.code} value={c.code}>{c.name}</option>
                    ))}
                  </optgroup>
                  {REGION_ORDER.map(region => (
                    <optgroup key={region} label={region}>
                      {(COUNTRIES_BY_REGION[region] || []).map(c => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </optgroup>
                  ))}
                </select></Field>
                {/* Iter 143 — service disciplines multi-select at signup. */}
                <Field label="Which services will you offer? (pick one or more)">
                  <div className="flex flex-wrap gap-1.5" data-testid="ob-disciplines">
                    {DISCIPLINES.map(d => {
                      const on = form.disciplines.includes(d.key);
                      return (
                        <button type="button" key={d.key} title={d.sub}
                                onClick={() => set('disciplines',
                                  on ? (form.disciplines.length > 1 ? form.disciplines.filter(x => x !== d.key) : form.disciplines)
                                     : [...form.disciplines, d.key])}
                                className={`px-2.5 py-1.5 rounded-full text-xs border transition-colors ${on ? 'bg-brand text-white border-brand' : 'surface border-soft text-fg-secondary hover:border-brand/50'}`}
                                data-testid={`ob-disc-${d.key}`}>
                          {on ? '✓ ' : ''}{d.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-fg-muted mt-1">Forms, examination templates and booking adapt to your selection. You can add more later in Settings → Service disciplines.</p>
                </Field>
                {/* Iter 98 — data residency picker. Iter 122 — expanded to
                    12 regions grouped by continent; option labels kept short
                    (country + city) so they fit without truncation, with the
                    full regulation info surfaced in the summary card below. */}
                <Field label="Where should your data live?">
                  <select className="input" value={form.data_residency} onChange={e => set('data_residency', e.target.value)} data-testid="ob-residency">
                    <optgroup label="Europe">
                      {RESIDENCY_REGIONS.filter(r => ['eu','uk','ch'].includes(r.code)).map(r => (
                        <option key={r.code} value={r.code} disabled={!r.available}>
                          {r.full}{r.default ? ' · default' : ''}{!r.available ? ' · coming soon' : ''}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Americas">
                      {RESIDENCY_REGIONS.filter(r => ['us','usw','ca','br'].includes(r.code)).map(r => (
                        <option key={r.code} value={r.code} disabled={!r.available}>
                          {r.full}{!r.available ? ' · coming soon' : ''}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Asia-Pacific & Middle East">
                      {RESIDENCY_REGIONS.filter(r => ['ap','sg','jp','ae','in'].includes(r.code)).map(r => (
                        <option key={r.code} value={r.code} disabled={!r.available}>
                          {r.full}{!r.available ? ' · coming soon' : ''}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <ResidencySummary key={form.data_residency} code={form.data_residency} />
                  <p className="text-[10px] text-fg-muted mt-1 leading-snug">Your selected data residency region is binding upon account creation and applies to all patient and clinical records stored thereafter. Selection of an applicable jurisdiction may be required to satisfy public-payer, hospital network, or statutory data-sovereignty obligations (including but not limited to NHS, KBV, SUS, and NDIS frameworks). Subsequent migration between residency regions is permitted only by written request to Physiogenic Support and may be subject to a controlled data-transfer procedure, fees, and minimum service-continuity periods.</p>
                </Field>
                {error && (
                  <div className="surface border border-[#B83A3A]/30 bg-[#B83A3A]/5 rounded-md p-3 flex items-start gap-2" data-testid="ob-error">
                    <span className="w-5 h-5 rounded-full bg-[#B83A3A] text-white inline-flex items-center justify-center text-xs font-bold shrink-0">!</span>
                    <div className="text-sm text-[#B83A3A]"><strong>{error}</strong>{error.toLowerCase().includes('already') && (
                      <> · <a href="/sign-in" className="underline">Sign in here</a></>
                    )}</div>
                  </div>
                )}
                <button type="submit" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2 text-base py-3" data-testid="ob-submit">
                  {busy ? 'Creating clinic…' : <>
                    {mode === 'magic' ? <>📩 Email me my sign-in link</> : <>Start 30-day free trial</>}
                    <ArrowRight className="w-4 h-4" />
                  </>}
                </button>
                <div className="text-center pt-1">
                  {mode === 'magic' ? (
                    <button type="button" onClick={()=>setMode('password')} className="text-xs text-fg-muted hover:text-brand underline" data-testid="ob-switch-password">
                      Prefer to set a password now? Switch to instant access
                    </button>
                  ) : (
                    <button type="button" onClick={()=>setMode('magic')} className="text-xs text-fg-muted hover:text-brand underline" data-testid="ob-switch-magic">
                      ← Back to magic link (recommended)
                    </button>
                  )}
                </div>
                <p className="text-xs text-fg-muted text-center mt-1">
                  Already have a clinic? <a href="/sign-in" className="text-brand underline">Sign in here</a>.
                </p>
                <p className="text-[11px] text-fg-muted text-center">
                  By creating a clinic you agree to our <a href="/legal/terms" className="text-brand underline">Terms</a> and <a href="/legal/privacy" className="text-brand underline">Privacy Policy</a>.
                </p>
              </>
            )}
          </form>
        </div>

        {/* Compare tiers — Iter 73: 3-column. */}
        <div className="mt-12">
          <h2 className="text-2xl font-heading font-bold mb-4 text-center">What's the difference between the three tiers?</h2>
          <div className="surface border border-soft rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-soft text-fg-muted uppercase text-xs">
                <tr>
                  <th className="text-left p-3">Feature</th>
                  <th className="text-center p-3">Solo</th>
                  <th className="text-center p-3 bg-[#FFB000]/10">Medium</th>
                  <th className="text-center p-3">Multi</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Price (billed monthly)',          '20€/user', '39€/user', 'from 45€/user'],
                  ['Practitioners',                    '1',         'up to 10',  'Unlimited'],
                  ['Locations',                        '1',         '1-2',       'Unlimited'],
                  ['Active patients',                  '200',       'Unlimited', 'Unlimited'],
                  ['Online booking + reminders',       '✓',         '✓',         '✓'],
                  ['Custom branding (logo + colors)',  '✓',         '✓',         '✓'],
                  ['Patient portal in EN / PL / ES',   '✓',         '✓',         '✓'],
                  ['GDPR + HIPAA records',             '✓',         '✓',         '✓'],
                  ['AI clinical summaries',            '✓',         '✓',         '✓'],
                  ['Receptionist + clinician roles + 2FA', '—',     '✓',         '✓'],
                  ['Stripe Connect payouts',           '—',         '✓',         '✓'],
                  ['Marketing email blasts',           '—',         '✓',         '✓'],
                  ['Product catalogue / storefront',   '—',         '✓',         '✓'],
                  ['Per-location reporting',           '—',         '✓',         '✓'],
                  ['AI Scribe + AI ICD suggester',     '—',         '✓',         '✓'],
                  ['Treatment packages',               '—',         '✓',         '✓'],
                  ['Multi-disciplinary care timeline', '—',         '—',         '✓'],
                  ['Layered body-chart progression',   '—',         '—',         '✓'],
                  ['Custom HEP video uploads',         '—',         '—',         '✓'],
                  ['White-label patient portal',       '—',         '—',         '✓'],
                  ['API + webhook access',             '—',         '—',         '✓'],
                  ['SSO / SAML for staff',             '—',         '—',         'On request'],
                  ['Dedicated onboarding manager',     '—',         '—',         '✓'],
                  ['Support',                          'Email',     'Priority email + chat', 'SLA + named'],
                ].map(([feat, a, b, c], i) => (
                  <tr key={i} className="border-t border-soft">
                    <td className="p-3">{feat}</td>
                    <td className="p-3 text-center text-fg-muted">{a}</td>
                    <td className="p-3 text-center font-semibold bg-[#FFB000]/5">{b}</td>
                    <td className="p-3 text-center text-fg-muted">{c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <footer className="text-center pb-8 text-xs text-fg-muted">
        <a href="/" className="hover:text-brand">← Back to Physiogenic</a>
      </footer>
    </div>
  );
}

function PerkRow({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-[#FFB000] mt-0.5 shrink-0"/>
      <div>
        <div className="font-semibold text-[#003D80]">{title}</div>
        <div className="text-fg-muted">{body}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block">
    <div className="text-xs text-fg-muted mb-1 font-medium">{label}</div>{children}
  </label>;
}


/**
 * Iter 122 — Selected-residency summary card.
 *
 * Rendered as a standalone component (rather than an inline IIFE) so React's
 * reconciler correctly re-runs on `code` change. The Babel `visual-edits`
 * plugin was freezing the inline version. We deliberately build every
 * displayed string via runtime concat (`.concat`, `+ ''`) so the plugin
 * can't statically precompute + cache the JSX text nodes.
 */
function ResidencySummary({ code }) {
  const r = getResidency(code);
  // Runtime-built strings that the visual-edits plugin can't freeze.
  const shortLabel = String(r.short);
  const regionLine = ['Atlas', '·', r.region].join(' ');
  const lawsPrefix = ['Applicable', 'laws:'].join(' ');
  const lawsBody = String(r.regulation);
  return (
    <div className="mt-2 rounded-md border border-soft bg-[#FAF6EE] px-3 py-2 text-xs" data-testid="ob-residency-summary">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="font-medium text-fg" data-testid={'ob-res-summary-short-' + code}>{shortLabel}</span>
        <span className="font-mono text-[10px] text-fg-muted" data-testid={'ob-res-summary-region-' + code}>{regionLine}</span>
      </div>
      <div className="mt-1 text-fg-muted">
        <span className="text-fg font-medium">{lawsPrefix}</span> <span data-testid={'ob-res-summary-laws-' + code}>{lawsBody}</span>
      </div>
    </div>
  );
}
